import { useState, useMemo } from 'react';
import { X, Key, Eye, EyeOff } from '@/components/icons';
import { Button } from './ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from './ui/drawer';
import { useAircraftGetAll, useDevicePasswordGetByAircraft } from '../lib/hooks';

interface ViewPasswordsDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  /** Registrations of the aircraft to show passwords for (one section per tail) */
  aircraftTails: string[];
}

export function ViewPasswordsDrawer({ isOpen, onClose, aircraftTails }: ViewPasswordsDrawerProps) {
  return (
    <Drawer open={isOpen} onOpenChange={onClose}>
      <DrawerContent className='bg-surface-L0 max-h-[85dvh]'>
        <DrawerHeader className='flex justify-between'>
          <DrawerClose asChild>
            <Button variant="secondary" size="icon" roundness="round" className="size-8">
              <X className="w-4 h-4" />
            </Button>
          </DrawerClose>
          <div className="flex items-center gap-2">
            <Key className="w-5 h-5 text-content-brand-bold" />
            <DrawerTitle>Passwords</DrawerTitle>
          </div>
          <div className="w-9" /> {/* Spacer for centering */}
        </DrawerHeader>

        <div className="flex-1 overflow-y-auto overscroll-contain" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="px-5 py-4 flex flex-col gap-6">
            {aircraftTails.length === 0 ? (
              <div className="bg-gray-50 rounded-[16px] p-8 text-center">
                <Key className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                <p className="font-normal text-text-content-primary-subtle text-[14px]">
                  Add a flight with an aircraft to see its passwords here
                </p>
              </div>
            ) : (
              aircraftTails.map((tail) => (
                <AircraftPasswordsSection key={tail} aircraftTail={tail} />
              ))
            )}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function AircraftPasswordsSection({ aircraftTail }: { aircraftTail: string }) {
  const [visiblePasswords, setVisiblePasswords] = useState<Set<string>>(new Set());

  // Get aircraft data to find ID
  const { data: allAircraft = [] } = useAircraftGetAll() as { data: any[] };

  // Find aircraft ID from tail number
  const aircraftId = useMemo(() => {
    const aircraft = allAircraft.find((a: any) => a.registration === aircraftTail || a.tail === aircraftTail);
    return aircraft?.id || '';
  }, [allAircraft, aircraftTail]);

  // Get device passwords for this aircraft
  const { data: passwords = [], isPending: loading } = useDevicePasswordGetByAircraft(aircraftId);

  const handleTogglePasswordVisibility = (id: string) => {
    setVisiblePasswords(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  return (
    <div>
      {/* Aircraft Info */}
      <div className="mb-4">
        <p className="font-normal text-content-tertiary-bold text-[12px] mb-1">
          Aircraft
        </p>
        <p className="font-semibold text-text-default text-[14px]">
          {aircraftTail}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="w-8 h-8 border-4 border-gray-300 border-t-brand-gold rounded-full animate-spin"></div>
        </div>
      ) : passwords.length > 0 ? (
        <div className="space-y-3">
          {passwords.map((password) => (
            <div
              key={password.id}
              className="bg-surface-L0 border border-border-L2 rounded-[6px] p-4"
            >
              {/* Device Name */}
              <div className="mb-3">
                <p className="font-normal text-content-tertiary-bold text-[12px] mb-1">
                  Device
                </p>
                <p className="font-['DM_Sans:Medium',sans-serif] text-text-default text-[14px]">
                  {password.deviceName}
                </p>
              </div>

              {/* Password */}
              <div>
                <p className="font-normal text-content-tertiary-bold text-[12px] mb-1">
                  Password
                </p>
                <div className="flex items-center gap-2">
                  <p className="font-['DM_Sans:Medium',sans-serif] text-text-default text-[14px] flex-1">
                    {visiblePasswords.has(password.id) ? password.password : '••••••••'}
                  </p>
                  <button
                    onClick={() => handleTogglePasswordVisibility(password.id)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    {visiblePasswords.has(password.id) ? (
                      <EyeOff className="w-4 h-4 text-gray-500" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-500" />
                    )}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-gray-50 rounded-[16px] p-8 text-center">
          <Key className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="font-normal text-text-content-primary-subtle text-[14px] mb-1">
            No passwords configured
          </p>
          <p className="font-normal text-text-content-primary-subtle text-[12px]">
            Add aircraft "{aircraftTail}" in Control Centre → Aircraft, then configure passwords
          </p>
        </div>
      )}
    </div>
  );
}
